import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PageHeader, EmptyState, ErrorBanner, LoadingRows, StatusPill, PriorityPill } from "../../components/app/ui";
import ReviewDrawer from "./ReviewDrawer";

const TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "reviewed", label: "Reviewed" },
];

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async (status = tab) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reviews", { params: status ? { status } : {} });
      setReviews(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load(tab);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: /reviews?open=<review_id> opens that case directly
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || selected) return;
    let cancelled = false;
    api.get("/reviews")
      .then(({ data }) => {
        if (cancelled) return;
        const match = data.find((r) => r.review_id === openId);
        if (match) setSelected(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [searchParams, selected]);

  return (
    <div data-testid="reviews-page">
      <PageHeader testId="reviews-header" title="AI Reviews" sub="Patient progress submissions waiting for clinician review. Click a case to open it." />

      <div className="flex items-start gap-3 rounded-2xl border border-[#2563EB]/20 bg-[#2563EB]/5 px-5 py-4 mb-7" data-testid="reviews-disclaimer">
        <ShieldCheck className="w-[18px] h-[18px] text-[#2563EB] shrink-0 mt-0.5" strokeWidth={1.7} />
        <p className="text-sm text-slate-600 leading-relaxed">
          AI output is advisory only. The clinician remains responsible for all clinical decisions.
        </p>
      </div>

      <div className="inline-flex gap-1 p-1 rounded-full bg-white border border-slate-200 mb-7" data-testid="reviews-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`reviews-tab-${t.label.toLowerCase()}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-5 py-2 text-xs font-semibold tracking-[0.12em] uppercase transition-colors duration-200 ${
              tab === t.key ? "bg-gradient-to-r from-[#2563EB]/10 to-[#6366F1]/10 text-[#1D4ED8] border border-[#2563EB]/20" : "text-slate-400 hover:text-slate-600 border border-transparent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ErrorBanner message={error} onRetry={() => load(tab)} />

      {loading ? (
        <LoadingRows rows={4} />
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
          <EmptyState testId="reviews-list-empty" title="No reviews here" sub="New patient submissions will appear in this queue." />
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((review) => (
            <div
              key={review.review_id}
              data-testid={`reviews-row-${review.review_id}`}
              onClick={() => setSelected(review)}
              className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] px-6 py-5 flex flex-wrap items-center gap-4 transition-[border-color,box-shadow] duration-300 hover:border-[#2563EB]/25 hover:shadow-[0_10px_26px_rgba(37,99,235,0.08)] cursor-pointer"
            >
              <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#2563EB]/80 to-[#6366F1]/80 flex items-center justify-center text-sm font-bold text-white shrink-0">
                {review.patient ? `${review.patient.first_name[0]}${review.patient.last_name[0]}` : "?"}
              </span>
              <div className="flex-1 min-w-[180px]">
                <p className="text-base font-medium text-[#0F172A]">
                  {review.patient ? `${review.patient.first_name} ${review.patient.last_name}` : "Unknown patient"}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {review.patient?.display_id} · {review.patient?.treatment_type} · Submitted {new Date(review.submitted_at).toLocaleString()}
                </p>
                {review.finding && (
                  <p className="text-xs text-slate-500 mt-1.5">{review.finding}</p>
                )}
                {review.status === "reviewed" && review.reviewed_at && (
                  <p className="text-xs text-[#0891B2] mt-0.5">Reviewed {new Date(review.reviewed_at).toLocaleString()}</p>
                )}
              </div>
              {review.priority && review.status === "pending" && <PriorityPill priority={review.priority} />}
              <StatusPill status={review.status} />
              {review.status === "pending" && (
                <button
                  type="button"
                  data-testid={`reviews-open-${review.review_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(review);
                  }}
                  className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.35)]"
                >
                  Review
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ReviewDrawer
        review={selected}
        onClose={() => {
          setSelected(null);
          if (searchParams.get("open")) setSearchParams({}, { replace: true });
        }}
        onUpdated={() => load(tab)}
      />
    </div>
  );
}
